import { FormikProps } from "formik";
import { useState } from "react";
import { ICCheckbox, ICTextInput } from "../../core-component/form";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  IAvailableClaim,
  IPackageEditorForm,
  MANAGE_SUBSCRIPTION_CLAIM_KEY,
} from "./PackageEditorController";

interface IPackageClaimsTabProps {
  formikProps: FormikProps<IPackageEditorForm>;
  availableClaims: IAvailableClaim[];
  readOnly: boolean;
}

interface IClaimGroup {
  name: string;
  claims: IAvailableClaim[];
}

const UNGROUPED = "";

const groupClaims = (claims: IAvailableClaim[], otherLabel: string): IClaimGroup[] => {
  const groups = new Map<string, IAvailableClaim[]>();
  for (const claim of claims) {
    const names = claim.claim_groups
      ? claim.claim_groups.split(",").map((name) => name.trim())
      : [UNGROUPED];
    for (const name of names) {
      groups.set(name, [...(groups.get(name) ?? []), claim]);
    }
  }
  return Array.from(groups.entries())
    .map(([name, groupClaimsList]) => ({ name: name || otherLabel, claims: groupClaimsList }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const PackageClaimsTab: React.FC<IPackageClaimsTabProps> = ({
  formikProps,
  availableClaims,
  readOnly,
}) => {
  const { t } = UseTranslationHook();
  const { values, setFieldValue } = formikProps;
  const [search, setSearch] = useState("");

  const lockedIds = availableClaims
    .filter((claim) => claim.claim_key === MANAGE_SUBSCRIPTION_CLAIM_KEY)
    .map((claim) => claim.id);
  const searchText = search.trim().toLowerCase();
  const groups = groupClaims(
    availableClaims.filter((claim) => claim.claim_name.toLowerCase().includes(searchText)),
    t("other"),
  );

  const setClaims = (ids: string[]) =>
    setFieldValue("claim_ids", Array.from(new Set([...ids, ...lockedIds])));

  const toggleClaim = (id: string, checked: boolean) =>
    setClaims(checked ? [...values.claim_ids, id] : values.claim_ids.filter((item) => item !== id));

  const toggleGroup = (group: IClaimGroup, checked: boolean) => {
    const groupIds = group.claims.map((claim) => claim.id);
    setClaims(
      checked
        ? [...values.claim_ids, ...groupIds]
        : values.claim_ids.filter((id) => !groupIds.includes(id)),
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <ICTextInput
            type="search"
            placeholder={t("search_claims")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <span className="text-sm text-muted whitespace-nowrap">
          {values.claim_ids.length} / {availableClaims.length}
        </span>
      </div>
      {groups.map((group) => {
        const selected = group.claims.filter((claim) => values.claim_ids.includes(claim.id)).length;
        return (
          <div key={group.name} className="border rounded-md">
            <div className="flex items-center justify-between bg-base px-3 py-2">
              <ICCheckbox
                label={group.name}
                checked={selected === group.claims.length}
                disabled={readOnly}
                onChange={(checked) => toggleGroup(group, checked)}
              />
              <span className="text-xs text-muted">
                {selected} / {group.claims.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3">
              {group.claims.map((claim) => (
                <ICCheckbox
                  key={claim.id}
                  label={claim.claim_name}
                  checked={values.claim_ids.includes(claim.id)}
                  disabled={readOnly || lockedIds.includes(claim.id)}
                  onChange={(checked) => toggleClaim(claim.id, checked)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PackageClaimsTab;
